// app/src/modules/auth/Login.tsx — sign-in (Bootstrap on design tokens).
// Single centred card: manager (email + password), till PIN, and email OTP
// step-up. Every state speaks through one <Notice> so messages read the same
// everywhere (error / success / info), and each step carries a heading,
// a one-line explanation, the form, then actions — in that order.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../lib/session";
import ThemeToggle from "../ui/ThemeToggle";
import BrandLockup from "../ui/Brand";
import { fieldErrorProps } from "../ui/field";

const DEMO_ORG = "org_slough";
const REMEMBER_KEY = "epos.login.email";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Notice = { kind: "error" | "success" | "info"; title: string; body?: string };

const primaryClass = "login-go";
const primaryStyle: React.CSSProperties = {};  // kept: spread at each call site
const spinnerStyle: React.CSSProperties = { width: 16, height: 16, borderRadius: 999, border: "2.5px solid rgba(255,255,255,.45)", borderTopColor: "#fff" };

/** Map transport/server errors onto messages a member of staff can act on. */
function describeError(e: any, ctx: "manager" | "till" | "otp"): Notice {
  const status: number | undefined = e?.status;
  const msg: string = String(e?.message ?? "");
  if (status === 0 || /failed to fetch|network|offline/i.test(msg))
    return { kind: "error", title: "Can't reach the server", body: "Check the internet connection and try again." };
  if (status === 429)
    return { kind: "error", title: "Too many attempts", body: msg || "Wait a few minutes before trying again." };
  if (ctx === "otp") {
    if (/expired/i.test(msg)) return { kind: "error", title: "Code expired", body: "Codes are valid for a short time. Go back and sign in again to receive a new one." };
    return { kind: "error", title: "Incorrect code", body: "Check the 6 digits in the message and try again." };
  }
  if (status === 401)
    return ctx === "till"
      ? { kind: "error", title: "PIN not recognised", body: "Check the PIN and try again, or ask a manager to reset it." }
      : { kind: "error", title: "Incorrect email or password", body: "Check both and try again. Passwords are case-sensitive." };
  if (status && status >= 500)
    return { kind: "error", title: "Something went wrong on our side", body: "Please try again in a moment." };
  return { kind: "error", title: "Sign-in failed", body: msg || "Please try again." };
}

export default function Login() {
  const { login, verifyOtp } = useSession();
  const nav = useNavigate();
  const [mode, setMode] = useState<"manager" | "till">("manager");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [pin, setPin] = useState("");
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [otp, setOtp] = useState<{ token: string; sentTo?: string; channel?: string } | null>(null);
  const [code, setCode] = useState("");
  const [fieldErr, setFieldErr] = useState<{ email?: boolean; password?: boolean; code?: boolean }>({});
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) { setEmail(saved); setRemember(true); }
  }, []);

  const clear = () => { setNotice(null); setFieldErr({}); };
  const isError = notice?.kind === "error";

  async function submit() {
    clear();
    if (mode === "manager") {
      const e = email.trim();
      if (!e || !password) {
        setFieldErr({ email: !e, password: !password });
        setNotice({ kind: "error", title: "Enter your email and password", body: !e && !password ? "Both fields are required." : !e ? "Email address is required." : "Password is required." });
        return;
      }
      if (!EMAIL_RE.test(e)) { setFieldErr({ email: true }); setNotice({ kind: "error", title: "Check the email address", body: "It doesn't look like a valid email." }); return; }
    } else if (pin.length < 4) {
      setNotice({ kind: "error", title: "PIN too short", body: "Enter at least 4 digits, then press OK." });
      return;
    }
    setBusy(true);
    try {
      const r = mode === "manager" ? await login({ email: email.trim(), password }) : await login({ organisation_id: DEMO_ORG, pin });
      if (mode === "manager") {
        if (remember) localStorage.setItem(REMEMBER_KEY, email.trim());
        else localStorage.removeItem(REMEMBER_KEY);
      }
      if (r?.otp_required && r.pending_token) {
        setOtp({ token: r.pending_token, sentTo: r.sent_to, channel: r.channel });
        setNotice({ kind: "success", title: "Verification code sent", body: r.channel === "whatsapp" ? `Check WhatsApp (${String(r.sent_to ?? "").replace(/^WhatsApp /, "")}).` : r.sent_to ? `Check the inbox for ${r.sent_to}.` : "Check your email inbox." });
        return;
      }
      setNotice({ kind: "success", title: "Signed in", body: "Opening your terminal…" });
      void nav("/");
    } catch (e: any) {
      setNotice(describeError(e, mode));
      if (mode === "manager") setFieldErr({ email: true, password: true });
      else setPin("");
    } finally { setBusy(false); }
  }

  async function submitOtp() {
    if (!otp) return;
    clear();
    if (code.length < 6) { setFieldErr({ code: true }); setNotice({ kind: "error", title: "Enter the 6-digit code" }); return; }
    setBusy(true);
    try {
      await verifyOtp(otp.token, code.trim());
      setNotice({ kind: "success", title: "Verified", body: "Opening your terminal…" });
      void nav("/");
    } catch (e: any) {
      setNotice(describeError(e, "otp"));
      setFieldErr({ code: true });
      setCode("");
      codeRef.current?.focus();
    } finally { setBusy(false); }
  }

  const backToSignIn = () => { setOtp(null); setCode(""); clear(); };
  const tapPin = (d: string) => { if (isError) clear(); setPin((p) => (d === "←" ? p.slice(0, -1) : (p + d).slice(0, 8))); };

  return (
    <div className="login-page">
      <div className="position-absolute" style={{ right: "max(16px,env(safe-area-inset-right))", top: "max(16px,env(safe-area-inset-top))", zIndex: 2 }}><ThemeToggle compact /></div>

      {/* Lockup above the card, centred, at every width. The speed-rule sits
          directly under it — the same order as the mark itself, where the
          tapered red line runs under SCOOTERS. */}
      <div className="login-lockup">
        <BrandLockup size={200} className="login-mark" />
        <hr className="login-speed" />
      </div>

      <main className="login-card" aria-label="Sign in">
        <div className="login-body">
          <header className="login-head">
            {otp ? (<>
              <h1 className="display login-title">{otp.channel === "whatsapp" ? "Check WhatsApp" : "Check your email"}</h1>
              <p className="login-sub">
                We sent a 6-digit code{otp.sentTo ? <> to <b style={{ color: "var(--ink)" }}>{otp.sentTo}</b></> : null}. Enter it to finish signing in.
              </p>
            </>) : (<>
              <h1 className="display login-title">Sign in</h1>
              <p className="login-sub">Email and password for back office. PIN for the till.</p>
            </>)}
          </header>

        {notice && <NoticeBox n={notice} />}

        {otp ? (
          <div className="d-grid" style={{ gap: 14 }}>
            <Field label="Verification code" htmlFor="login-otp">
              <input ref={codeRef} id="login-otp" className="login-in" style={{ textAlign: "center", fontSize: 22, letterSpacing: 8, fontFamily: "var(--mono)" }} value={code}
                inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6}
                {...fieldErrorProps(!!fieldErr.code, "login-notice")}
                onChange={(e) => { if (isError) clear(); setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); }}
                onKeyDown={(e) => e.key === "Enter" && submitOtp()} placeholder="••••••" />
            </Field>
            <button className={primaryClass} style={{ ...primaryStyle, opacity: busy ? 0.75 : 1 }} disabled={busy} onClick={submitOtp}>
              {busy ? <><span className="spin d-inline-block" style={spinnerStyle} /> Verifying…</> : "Verify & sign in"}
            </button>
            <div className="d-grid" style={{ gap: 6, textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
              <span>Didn&apos;t get it? Check your junk folder — the code expires after a few minutes.</span>
              <button type="button" className="login-link" onClick={backToSignIn}>Sign in again to get a new code</button>
            </div>
          </div>
        ) : (<>
          {/* One signal for "selected" — the raised surface. It previously had a
              raised surface AND a red underline, which said the same thing
              twice and spent red on a state rather than on the action. */}
          <div className="login-seg" role="tablist" aria-label="Sign-in method">
            {([["manager", "Manager"], ["till", "Till PIN"]] as const).map(([k, label]) => (
              <button key={k} role="tab" aria-selected={mode === k}
                onClick={() => { setMode(k); clear(); setPin(""); }}>{label}</button>
            ))}
          </div>

          {mode === "manager" ? (
            <div className="d-grid" style={{ gap: 14 }}>
              <Field label="Email address" htmlFor="login-email">
                <input id="login-email" className="login-in" value={email} autoFocus type="email" autoComplete="username"
                  {...fieldErrorProps(!!fieldErr.email, "login-notice")}
                  onChange={(e) => { if (isError) clear(); setEmail(e.target.value); }}
                  onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="name@sloughscooters.com" />
              </Field>
              <Field label="Password" htmlFor="login-password">
                <div className="position-relative">
                  <input id="login-password" className="login-in" style={{ paddingRight: 74 }} type={showPw ? "text" : "password"} value={password}
                    autoComplete="current-password" {...fieldErrorProps(!!fieldErr.password, "login-notice")}
                    onChange={(e) => { if (isError) clear(); setPassword(e.target.value); }}
                    onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Your password" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} aria-pressed={showPw}
                    className="login-link position-absolute" style={{ right: 6, top: "50%", transform: "translateY(-50%)", padding: "6px 8px", fontSize: 13, textDecoration: "none" }}>
                    {showPw ? "Hide" : "Show"}
                  </button>
                </div>
              </Field>
              <div className="d-flex align-items-center justify-content-between" style={{ gap: 12 }}>
                <label className="d-flex align-items-center fw-semibold" style={{ cursor: "pointer", gap: 8, fontSize: 13, color: "var(--ink-2)" }}>
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ height: 16, width: 16, accentColor: "var(--accent)" }} />
                  Remember my email
                </label>
                <button type="button" className="login-link" aria-expanded={forgot} onClick={() => setForgot((f) => !f)}>Forgot password?</button>
              </div>
              {forgot && <NoticeBox n={{ kind: "info", title: "Password resets are done by an administrator", body: "Ask an owner or manager to reset your password from Back office › Staff. You'll receive a new one to sign in with." }} />}
              <button className={primaryClass} style={{ ...primaryStyle, opacity: busy ? 0.75 : 1 }} disabled={busy} onClick={submit}>
                {busy ? <><span className="spin d-inline-block" style={spinnerStyle} /> Signing in…</> : "Sign in"}
              </button>
            </div>
          ) : (
            <div className="d-grid" style={{ gap: 14 }}>
              <div className="num d-grid" style={{ height: 58, placeItems: "center", borderRadius: 4, border: `1.5px solid ${isError ? "var(--accent)" : "var(--line)"}`, background: "var(--paper)", fontSize: 26, letterSpacing: 6, color: "var(--ink)" }} aria-label="PIN entry" aria-live="polite">
                {pin.replace(/./g, "•") || <span style={{ fontSize: 15, letterSpacing: "normal", color: "var(--muted)" }}>Enter your till PIN</span>}
              </div>
              <div className="d-grid" style={{ gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "←", "0", "OK"].map((k) => (
                  <button key={k} className="pinkey fw-bold" disabled={busy && k === "OK"}
                    style={{ height: 58, borderRadius: 4, fontSize: "1.25rem", ...(k === "OK" ? { background: "var(--accent)", color: "var(--accent-ink)", opacity: busy ? 0.75 : 1 } : { background: "var(--paper-2)", color: "var(--ink)" }) }}
                    aria-label={k === "←" ? "Delete" : k === "OK" ? "Confirm PIN" : `Digit ${k}`}
                    onClick={() => (k === "OK" ? submit() : tapPin(k))}>{k === "OK" && busy ? <span className="spin d-inline-block" style={spinnerStyle} /> : k}</button>
                ))}
              </div>
              <p style={{ margin: 0, fontSize: 13, color: "var(--muted)", textAlign: "center" }}>Type your PIN and press OK. Forgotten it? A manager can reset it in Staff.</p>
            </div>
          )}
        </>)}
        </div>
      </main>

      <p className="login-foot" style={{ gridColumn: "1 / -1", marginTop: 22, marginBottom: 0 }}>
        SESSIONS EXPIRE AFTER 24 HOURS
      </p>
    </div>
  );
}

const NOTICE: Record<Notice["kind"], { fg: string; bg: string; icon: string; label: string }> = {
  error:   { fg: "var(--accent-text)",  bg: "var(--red-soft)",   icon: "!", label: "Error" },
  success: { fg: "var(--success)", bg: "var(--green-soft)", icon: "✓", label: "Success" },
  info:    { fg: "var(--ink-2)",   bg: "var(--paper-2)",    icon: "i", label: "Information" },
};

function NoticeBox({ n }: { n: Notice }) {
  const t = NOTICE[n.kind];
  return (
    <div id="login-notice" role={n.kind === "error" ? "alert" : "status"} className="d-flex"
      style={{ gap: 10, padding: "10px 12px", borderRadius: 4, background: t.bg, border: `1px solid color-mix(in srgb, ${t.fg} 30%, transparent)` }}>
      <span aria-hidden className="d-grid fw-bold flex-shrink-0" style={{ placeItems: "center", width: 20, height: 20, borderRadius: 999, background: t.fg, color: "#fff", fontSize: 12, marginTop: 1 }}>{t.icon}</span>
      <div style={{ fontSize: 13, lineHeight: 1.45 }}>
        <span className="visually-hidden">{t.label}: </span>
        <div className="fw-bold" style={{ color: n.kind === "info" ? "var(--ink)" : t.fg }}>{n.title}</div>
        {n.body && <div style={{ color: "var(--ink-2)" }}>{n.body}</div>}
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="d-grid" style={{ gap: 6 }}>
      <span className="login-lab">{label}</span>
      {children}
    </label>
  );
}
